"use client";
import React, {ReactNode, useMemo, useRef, useState} from "react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Grid2,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    useTheme
} from "@mui/material";
import {QuantityOptionDTO, QuoteDTO, RequestDTO, UserDisplayDTO} from "@/api/data-contracts";
import {TFunction} from "i18next";
import {getI18n, useTranslation} from "react-i18next";
import {EDGE_CRUSH_UNIT, GSM, TOOLTIP_DELAY_MS} from "@/constants";
import {DateFormatter} from "@/lib/components/DateFormate";
import {stringField} from "@/common/fields/string-field";
import {dateRangeField} from "@/common/fields/date-range-field";
import {dateField} from "@/common/fields/date-field";
import {integerField} from "@/common/fields/integer-field";
import {booleanField} from "@/common/fields/boolean-field";
import {stringArrayField} from "@/common/fields/string-array-field";
import {currencyField} from "@/common/fields/currency-field";
import {CommonI18N, RequestI18N} from "@/translations";
import {IconButtonEx} from "@/lib/components/buttons/IconButtonEx";
import {Close, Fullscreen} from "@mui/icons-material";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {RequestStatusBadge} from "@/common/RequestStatusBadge";
import {QuoteStatusBadge} from "@/common/QuoteStatusBadge";
import {formatCurrency, formatInteger, formatNumber} from "@/lib/utils";
import {ShortCutHandler} from "@/lib/shortcut-handler";
import {withAuthWrapperComponent} from "@/app/auth/WithAuthWrapperComponent";
import {formatAddress} from "@/model/address";
import {formatUserName} from "@/model/user";
import {formatCustomerRequestNumber} from "@/model/request";
import dayjs from "dayjs";
import isEqual from "react-fast-compare";
import {color_blue, color_green, color_grey} from "@/agentur/AgenturColors";

const tableCellContentNoWrap = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block"
};

type QuoteMatrixRowProps = {
    quotes: QuoteDTO[];
    label: string;
    requestValue: ReactNode;
    renderQuoteValue: (quote: QuoteDTO) => ReactNode;
    checkQuoteDiff?: (quote: QuoteDTO) => boolean;
};

export function formatContact(t: (key: string) => string, user?: UserDisplayDTO): ReactNode {
    if (user == null) {
        return [];
    }
    return (
            <div>
                <div>{formatUserName(t, user)}</div>
                <div>
                    <a href={`tel:${user.telephone}`}>{user.telephone}</a>
                </div>
                <div>
                    <a href={`mailto:${user.email}`}>{user.email}</a>
                </div>
            </div>
    );
}

const getTableCellContent = (value: ReactNode) => (
        <Tooltip title={value} enterDelay={TOOLTIP_DELAY_MS} enterNextDelay={TOOLTIP_DELAY_MS}>
            <span style={tableCellContentNoWrap}>{value}</span>
        </Tooltip>
);

const QuoteMatrixRow = ({quotes, label, requestValue, renderQuoteValue, checkQuoteDiff}: QuoteMatrixRowProps) => (
        <TableRow>
            <TableCell>{label}</TableCell>
            <TableCell className="gap"/>
            <TableCell>{requestValue}</TableCell>
            {quotes.map((quote) => (
                    <React.Fragment key={quote.id}>
                        <TableCell className="gap"/>
                        <TableCell
                                className={checkQuoteDiff?.(quote) ? "diff" : undefined}>
                            <span> {renderQuoteValue(quote)}</span>
                        </TableCell>
                    </React.Fragment>
            ))}
            <TableCell className="gap"/>
        </TableRow>
);

const pickDiffClass = <T = unknown, >(a: T, b: T): string | undefined => a === b ? undefined : "diff";

const Prices = ({
                    quantityOptions,
                    orgOptions,
                    t,
                    locale
                }: {
    quantityOptions: QuantityOptionDTO[];
    orgOptions?: QuantityOptionDTO[];
    t: TFunction;
    locale: string;
}) => {
    if (quantityOptions == null) {
        return null;
    }
    return (
            <Table sx={{"& td, & th": {px: 2, py: 0.5, textAlign: "right"}}}>
                <TableHead>
                    <TableRow>
                        <TableCell>{t(RequestI18N.details.column.quantity)}</TableCell>
                        <TableCell>{t(RequestI18N.details.column.pricePerThousand)}</TableCell>
                        <TableCell>{t(RequestI18N.details.column.total)}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {quantityOptions.map((option, index) => (
                            <TableRow key={option.quantity}>
                                <TableCell
                                        className={orgOptions && pickDiffClass(option.quantity, orgOptions[index].quantity)}>
                                    <span>{formatInteger(option.quantity, locale)}</span>
                                </TableCell>
                                <TableCell
                                        className={
                                                orgOptions && pickDiffClass(option.netPricePer1000, orgOptions[index].netPricePer1000)
                                        }
                                >
                                    <span>{formatCurrency(option.netPricePer1000, locale)}</span>
                                </TableCell>
                                <TableCell
                                        className={
                                                orgOptions && pickDiffClass(option.netPriceTotal, orgOptions[index].netPriceTotal)
                                        }
                                >
                                    <span>{formatCurrency(option.netPriceTotal, locale)}</span>
                                </TableCell>
                            </TableRow>
                    ))}
                </TableBody>
            </Table>
    );
};

type GradeFieldRowProps = {
    label: string;
    value: React.ReactNode;
    highlighted: boolean;
    labelFontSize?: string;
};

const GradeFieldRow: React.FC<GradeFieldRowProps> = ({label, value, highlighted, labelFontSize = "0.8em"}) => {
    const theme = useTheme();
    return (
        <div style={{display: "flex", alignItems: "center"}}>
            <div style={{color: theme.palette.text.secondary, fontSize: labelFontSize, width: "150px", flexShrink: 0}}>
                {label}:
            </div>
            <div style={highlighted ? {backgroundColor: color_green[2], borderRadius: 4, padding: "2px 4px"} : {}}>
                {value}
            </div>
        </div>
    );
};

type QuoteCartonGradeValueProps = {
    quote: QuoteDTO;
    model: RequestDTO;
    locale: string;
};

const QuoteCartonGradeValue: React.FC<QuoteCartonGradeValueProps> = ({quote, model, locale}) => {
    const {t} = useTranslation();
    const grade = quote.orderData?.cartonGrade;
    const modelGrade = model.orderData?.cartonGrade;
    if (!grade) {
        return <div/>;
    }
    const weightDiffers =
        (!modelGrade?.weight && grade.weight) ||
        (!grade.weight && modelGrade?.weight) ||
        (grade.weight && modelGrade?.weight && grade.weight.toString() !== modelGrade.weight.toString());
    const ectDiffers =
        (!modelGrade?.edgeCrushTest && grade.edgeCrushTest) ||
        (!grade.edgeCrushTest && modelGrade?.edgeCrushTest) ||
        (grade.edgeCrushTest && modelGrade?.edgeCrushTest && grade.edgeCrushTest.toString() !== modelGrade.edgeCrushTest.toString());

    return (
        <div>
            <div style={{marginTop: "8px", display: "flex", flexDirection: "column", gap: "8px"}}>
                <GradeFieldRow
                    label={t(RequestI18N.grade.form.qualityGradeLabel)}
                    value={stringField(grade.code, "variety").value}
                    highlighted={grade.code !== modelGrade?.code}
                    labelFontSize="0.9em"
                />
                <GradeFieldRow
                    label={t(RequestI18N.grade.table.flute)}
                    value={grade.flute ?? "-"}
                    highlighted={grade.flute !== modelGrade?.flute}
                    labelFontSize="0.9em"
                />
                <GradeFieldRow
                    label={t(RequestI18N.grade.table.adOuterLiner)}
                    value={grade.outerLiner ?? "-"}
                    highlighted={grade.outerLiner !== modelGrade?.outerLiner}
                />
                <GradeFieldRow
                    label={t(RequestI18N.grade.table.w1Flute)}
                    value={grade.fluteOne ?? "-"}
                    highlighted={grade.fluteOne !== modelGrade?.fluteOne}
                />
                <GradeFieldRow
                    label={t(RequestI18N.grade.table.zd1Liner)}
                    value={grade.linerOne ?? "-"}
                    highlighted={grade.linerOne !== modelGrade?.linerOne}
                />
                <GradeFieldRow
                    label={t(RequestI18N.grade.table.w2Flute)}
                    value={grade.fluteTwo ?? "-"}
                    highlighted={grade.fluteTwo !== modelGrade?.fluteTwo}
                />
                <GradeFieldRow
                    label={t(RequestI18N.grade.table.zd2Liner)}
                    value={grade.linerTwo ?? "-"}
                    highlighted={grade.linerTwo !== modelGrade?.linerTwo}
                />
                <GradeFieldRow
                    label={t(RequestI18N.grade.table.w3Flute)}
                    value={grade.fluteThree ?? "-"}
                    highlighted={grade.fluteThree !== modelGrade?.fluteThree}
                />
                <GradeFieldRow
                    label={t(RequestI18N.grade.table.idInnerLiner)}
                    value={grade.innerLiner ?? "-"}
                    highlighted={grade.innerLiner !== modelGrade?.innerLiner}
                />
                <GradeFieldRow
                    label={t(RequestI18N.grade.table.weightGM2)}
                    value={`${formatNumber(grade.weight, 2, locale) ?? "-"} ${GSM}`}
                    highlighted={Boolean(weightDiffers)}
                />
                <GradeFieldRow
                    label={t(RequestI18N.grade.table.ectEdgeCrush)}
                    value={`${formatNumber(grade.edgeCrushTest, 2, locale) ?? "-"} ${EDGE_CRUSH_UNIT}`}
                    highlighted={Boolean(ectDiffers)}
                />
                <GradeFieldRow
                    label={t(RequestI18N.grade.table.nf)}
                    value={booleanField(grade.wetStrength, t(CommonI18N.message.yes), t(CommonI18N.message.no), t(RequestI18N.grade.table.nf), "-").value}
                    highlighted={grade.wetStrength !== modelGrade?.wetStrength}
                />
            </div>
        </div>
    );
};

const QuoteMatrixDialog = ({
                               open,
                               close,
                               model,
                               setQuote
                           }: {
    open: boolean;
    close: () => void;
    model: RequestDTO;
    setQuote: (quote: QuoteDTO) => void;
}) => {
    const {t} = useTranslation();
    const theme = useTheme();

    const locale = getI18n().language;
    const dateFormat = DateFormatter();

    const [fullScreen, setFullScreen] = useState(false);

    const quotes = useMemo(() => {
        const allQuotes = model.quotes ?? [];
        // Group by company and keep only the latest version per company
        const latestByCompany = new Map<number, QuoteDTO>();
        for (const quote of allQuotes) {
            const companyId = quote.company?.id ?? 0;
            const existing = latestByCompany.get(companyId);
            if (existing == null || (quote.quoteVersion ?? 1) > (existing.quoteVersion ?? 1)) {
                latestByCompany.set(companyId, quote);
            }
        }
        return [...latestByCompany.values()];
    }, [model.quotes]);

    const scrollRef = useRef<HTMLDivElement>(null);

    const shortcuts = {
        ArrowLeft: () => scrollRef.current?.scrollBy({left: -336, behavior: "smooth"}),
        ArrowRight: () => scrollRef.current?.scrollBy({left: 336, behavior: "smooth"})
    };

    return (
            <Dialog open={open} onClose={close} fullWidth maxWidth="xl" scroll="paper" fullScreen={fullScreen}>
                <DialogTitle>
                    <Grid2 container direction="row">
                        <Grid2 flexGrow={1}>
                            {t(RequestI18N.details.action.quoteOverview, {boxhubNumber: model?.boxhubNumber})}
                        </Grid2>
                        <Grid2>
                            <IconButtonEx Icon={Fullscreen} onClick={() => setFullScreen(!fullScreen)}/>
                        </Grid2>
                        <Grid2>
                            <IconButtonEx Icon={Close} onClick={() => close()}/>
                        </Grid2>
                    </Grid2>
                </DialogTitle>
                <DialogContent sx={{padding: 2}}>
                    <ShortCutHandler shortcuts={shortcuts}>
                        <TableContainer
                                ref={scrollRef}
                                sx={{
                                    width: "100%",
                                    overflow: "auto",
                                    maxHeight: "80vh",
                                    "& span, & td, & th": {
                                        color: "#333"
                                    },
                                    "&>table": {
                                        borderCollapse: "separate",
                                        width: "max-content"
                                    },
                                    "&>table>thead>tr th": {
                                        position: "sticky",
                                        height: 20,
                                        top: 0,
                                        zIndex: 12
                                    },

                                    "&>table>thead>tr>th:first-child,&>table>tbody>tr>td:first-child": {
                                        position: "sticky",
                                        minWidth: 240,
                                        maxWidth: 240,
                                        left: 0,
                                        zIndex: 13,
                                        textAlign: "right"
                                    },
                                    "&>table>thead>tr>th:nth-child(2), &>table>tbody>tr>td:nth-child(2)": {
                                        position: "sticky",
                                        left: 240,
                                        zIndex: 13
                                    },
                                    "&>table>thead>tr>th:nth-child(2)": {
                                        zIndex: 14
                                    },
                                    "&>table>thead>tr>th:nth-child(3), &>table>tbody>tr>td:nth-child(3)": {
                                        position: "sticky",
                                        left: 240 + 32,
                                        zIndex: 13
                                    },
                                    "&>table>thead>tr>th:nth-child(3)": {
                                        zIndex: 14
                                    },
                                    "&>table>thead>tr>th:nth-child(4), &>table>tbody>tr>td:nth-child(4)": {
                                        position: "sticky",
                                        left: 240 + 32 + 320,
                                        zIndex: 13
                                    },
                                    "&>table>thead>tr>th:nth-child(4)": {
                                        zIndex: 14
                                    },
                                    "&>table>tbody>tr>td, &>table>thead>tr>th": {
                                        border: "none",
                                        width: 320,
                                        maxWidth: 320,
                                        minWidth: 320,
                                        zIndex: 10
                                    },
                                    "& td.gap, & th.gap": {
                                        width: 16,
                                        maxWidth: 16,
                                        minWidth: 16
                                        // backgroundColor: theme.palette.background.default
                                    },

                                    "& td.diff>*": {
                                        // border: "1px solid",
                                        borderRadius: 0.5,
                                        // padding: 0.2,
                                        backgroundColor: `${color_green[2]}!important`
                                    },

                                    // "& td>div": {
                                    //     marginLeft: 4,
                                    //     marginRight: 4
                                    // },
                                    "&>table>tbody>tr:nth-child(odd)>td:not(.gap):nth-child(1), &>table>tbody>tr:nth-child(odd)>td:not(.gap):nth-child(3)":
                                            {
                                                backgroundColor: color_grey[3]
                                            },
                                    "&>table>tbody>tr:nth-child(odd)>td:not(.gap)": {
                                        backgroundColor: color_blue[4]
                                    },
                                    "&>table>tbody>tr:nth-child(even)>td:not(.gap):nth-child(1), &>table>thead>tr:nth-child(odd)>th:not(.gap):nth-child(1), &>table>thead>tr:nth-child(odd)>th:not(.gap):nth-child(3), &>table>tbody>tr:nth-child(even)>td:not(.gap):nth-child(3)":
                                            {
                                                backgroundColor: color_grey[2]
                                            },
                                    "&>table>tbody>tr:nth-child(even)>td:not(.gap), &>table>thead>tr:nth-child(odd)>th:not(.gap)":
                                            {
                                                backgroundColor: color_blue[3]
                                            }
                                    // "& tr:hover, & tr:hover td": {
                                    //     backgroundColor: theme.palette.mode === "dark" ? alpha(brown[900], 0.7) : yellow[400]
                                    // }
                                }}
                        >
                            <Table>
                                <TableHead>
                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.label.companyName)}
                                            requestValue={t(RequestI18N.details.label.myRequest)}
                                            renderQuoteValue={(quote) => (
                                                    <Grid2 container direction="row" spacing={2} alignItems="center">
                                                        <Grid2 flexGrow={1}>
                                                            {getTableCellContent(
                                                                    stringField(
                                                                            quote.company?.name,
                                                                            "companyName",
                                                                            t(CommonI18N.message.notGiven)
                                                                    ).value
                                                            )}
                                                        </Grid2>
                                                        <ButtonEx
                                                                label={t(RequestI18N.details.action.show)}
                                                                size="small"
                                                                onClick={() => {
                                                                    setQuote(quote);
                                                                    close();
                                                                }}
                                                                color="info"
                                                                variant="outlined"
                                                        />
                                                    </Grid2>
                                            )}
                                    />
                                </TableHead>

                                <TableBody>
                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.field.companyAddress)}
                                            requestValue={formatAddress(t, model.company.companyAddress, locale).map((e) => (
                                                    <div key={e}>{e}</div>
                                            ))}
                                            renderQuoteValue={(quote) =>
                                                    formatAddress(t, quote.company.companyAddress, locale).map((e) => (
                                                            <div key={e}>{e}</div>
                                                    ))
                                            }
                                    />
                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.field.contactPerson)}
                                            requestValue={formatContact(t, model.contactPerson)}
                                            renderQuoteValue={(quote) => formatContact(t, quote.contactPerson)}
                                    />
                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.field.customerQuoteNumber)}
                                            requestValue={formatCustomerRequestNumber(model)}
                                            renderQuoteValue={(quote) => formatCustomerRequestNumber(quote)}
                                    />

                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.field.quoteValidity)}
                                            requestValue={model?.validUntil && dayjs(model.validUntil).format(dateFormat)}
                                            checkQuoteDiff={(quote) => model?.validUntil !== quote.validUntil}
                                            renderQuoteValue={(quote) =>
                                                    quote?.validUntil && dayjs(quote.validUntil).format(dateFormat)
                                            }
                                    />

                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.field.additionalInformation)}
                                            requestValue={model.additionalInformation}
                                            renderQuoteValue={() => null}
                                    />

                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.field.quoteInformation)}
                                            requestValue={model.additionalInformation}
                                            checkQuoteDiff={(quote) => quote.additionalInformation != null}
                                            renderQuoteValue={(quote) => quote.additionalInformation}
                                    />

                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.field.paymentTerms)}
                                            requestValue={model.orderData.paymentTerms}
                                            checkQuoteDiff={(quote) =>
                                                    !isEqual(quote.orderData.paymentTerms, model.orderData.paymentTerms)
                                            }
                                            renderQuoteValue={(quote) => quote.orderData.paymentTerms}
                                    />

                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.field.deliveryDate)}
                                            requestValue={
                                                dateRangeField(
                                                        model?.orderData.deliveryDateFrom,
                                                        model?.orderData.deliveryDateTo,
                                                        "deliveryDateRange",
                                                        undefined,
                                                        dateFormat
                                                ).value
                                            }
                                            checkQuoteDiff={(quote) =>
                                                    !!quote.orderData?.deliveryDateFrom &&
                                                    dayjs(model.orderData.deliveryDateFrom).isBefore(
                                                            quote.orderData?.deliveryDateFrom
                                                    )
                                            }
                                            renderQuoteValue={(quote) =>
                                                    dateField(
                                                            quote.orderData?.deliveryDateFrom,
                                                            "deliveryDateLabel",
                                                            undefined,
                                                            dateFormat
                                                    ).value
                                            }
                                    />
                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.field.variety)}
                                            requestValue={
                                                <div>
                                                    {model?.orderData?.cartonGrade && (
                                                            <div
                                                                    style={{
                                                                        marginTop: "8px",
                                                                        display: "flex",
                                                                        flexDirection: "column",
                                                                        gap: "8px"
                                                                    }}
                                                            >
                                                                <div style={{display: "flex", alignItems: "center"}}>
                                                                    <div
                                                                            style={{
                                                                                color: theme.palette.text.secondary,
                                                                                fontSize: "0.9em",
                                                                                width: "150px",
                                                                                flexShrink: 0
                                                                            }}
                                                                    >
                                                                        {t(RequestI18N.grade.form.qualityGradeLabel)}:
                                                                    </div>
                                                                    <div>
                                                                        {
                                                                            stringField(
                                                                                    model?.orderData?.cartonGrade?.code,
                                                                                    "variety"
                                                                            ).value
                                                                        }
                                                                    </div>
                                                                </div>
                                                                <div style={{display: "flex", alignItems: "center"}}>
                                                                    <div
                                                                            style={{
                                                                                color: theme.palette.text.secondary,
                                                                                fontSize: "0.9em",
                                                                                width: "150px",
                                                                                flexShrink: 0
                                                                            }}
                                                                    >
                                                                        {t(RequestI18N.grade.table.flute)}:
                                                                    </div>
                                                                    <div>{model.orderData.cartonGrade.flute ?? "-"}</div>
                                                                </div>
                                                                <div style={{display: "flex", alignItems: "center"}}>
                                                                    <div
                                                                            style={{
                                                                                color: theme.palette.text.secondary,
                                                                                fontSize: "0.8em",
                                                                                width: "150px",
                                                                                flexShrink: 0
                                                                            }}
                                                                    >
                                                                        {t(RequestI18N.grade.table.adOuterLiner)}:
                                                                    </div>
                                                                    <div>{model.orderData.cartonGrade.outerLiner ?? "-"}</div>
                                                                </div>
                                                                <div style={{display: "flex", alignItems: "center"}}>
                                                                    <div
                                                                            style={{
                                                                                color: theme.palette.text.secondary,
                                                                                fontSize: "0.8em",
                                                                                width: "150px",
                                                                                flexShrink: 0
                                                                            }}
                                                                    >
                                                                        {t(RequestI18N.grade.table.w1Flute)}:
                                                                    </div>
                                                                    <div>{model.orderData.cartonGrade.fluteOne ?? "-"}</div>
                                                                </div>
                                                                <div style={{display: "flex", alignItems: "center"}}>
                                                                    <div
                                                                            style={{
                                                                                color: theme.palette.text.secondary,
                                                                                fontSize: "0.8em",
                                                                                width: "150px",
                                                                                flexShrink: 0
                                                                            }}
                                                                    >
                                                                        {t(RequestI18N.grade.table.zd1Liner)}:
                                                                    </div>
                                                                    <div>{model.orderData.cartonGrade.linerOne ?? "-"}</div>
                                                                </div>
                                                                <div style={{display: "flex", alignItems: "center"}}>
                                                                    <div
                                                                            style={{
                                                                                color: theme.palette.text.secondary,
                                                                                fontSize: "0.8em",
                                                                                width: "150px",
                                                                                flexShrink: 0
                                                                            }}
                                                                    >
                                                                        {t(RequestI18N.grade.table.w2Flute)}:
                                                                    </div>
                                                                    <div>{model.orderData.cartonGrade.fluteTwo ?? "-"}</div>
                                                                </div>
                                                                <div style={{display: "flex", alignItems: "center"}}>
                                                                    <div
                                                                            style={{
                                                                                color: theme.palette.text.secondary,
                                                                                fontSize: "0.8em",
                                                                                width: "150px",
                                                                                flexShrink: 0
                                                                            }}
                                                                    >
                                                                        {t(RequestI18N.grade.table.zd2Liner)}:
                                                                    </div>
                                                                    <div>{model.orderData.cartonGrade.linerTwo ?? "-"}</div>
                                                                </div>
                                                                <div style={{display: "flex", alignItems: "center"}}>
                                                                    <div
                                                                            style={{
                                                                                color: theme.palette.text.secondary,
                                                                                fontSize: "0.8em",
                                                                                width: "150px",
                                                                                flexShrink: 0
                                                                            }}
                                                                    >
                                                                        {t(RequestI18N.grade.table.w3Flute)}:
                                                                    </div>
                                                                    <div>{model.orderData.cartonGrade.fluteThree ?? "-"}</div>
                                                                </div>
                                                                <div style={{display: "flex", alignItems: "center"}}>
                                                                    <div
                                                                            style={{
                                                                                color: theme.palette.text.secondary,
                                                                                fontSize: "0.8em",
                                                                                width: "150px",
                                                                                flexShrink: 0
                                                                            }}
                                                                    >
                                                                        {t(RequestI18N.grade.table.idInnerLiner)}:
                                                                    </div>
                                                                    <div>{model.orderData.cartonGrade.innerLiner ?? "-"}</div>
                                                                </div>
                                                                <div style={{display: "flex", alignItems: "center"}}>
                                                                    <div
                                                                            style={{
                                                                                color: theme.palette.text.secondary,
                                                                                fontSize: "0.8em",
                                                                                width: "150px",
                                                                                flexShrink: 0
                                                                            }}
                                                                    >
                                                                        {t(RequestI18N.grade.table.weightGM2)}:
                                                                    </div>
                                                                    <div>{`${formatNumber(model.orderData.cartonGrade.weight, 2, locale) ?? "-"} ${GSM}`}</div>
                                                                </div>
                                                                <div style={{display: "flex", alignItems: "center"}}>
                                                                    <div
                                                                            style={{
                                                                                color: theme.palette.text.secondary,
                                                                                fontSize: "0.8em",
                                                                                width: "150px",
                                                                                flexShrink: 0
                                                                            }}
                                                                    >
                                                                        {t(RequestI18N.grade.table.ectEdgeCrush)}:
                                                                    </div>
                                                                    <div>{`${formatNumber(model.orderData.cartonGrade.edgeCrushTest, 2, locale) ?? "-"} ${EDGE_CRUSH_UNIT}`}</div>
                                                                </div>
                                                                <div style={{display: "flex", alignItems: "center"}}>
                                                                    <div
                                                                            style={{
                                                                                color: theme.palette.text.secondary,
                                                                                fontSize: "0.8em",
                                                                                width: "150px",
                                                                                flexShrink: 0
                                                                            }}
                                                                    >
                                                                        {t(RequestI18N.grade.table.nf)}:
                                                                    </div>
                                                                    <div>
                                                                        {
                                                                            booleanField(
                                                                                    model.orderData.cartonGrade.wetStrength,
                                                                                    t(CommonI18N.message.yes),
                                                                                    t(CommonI18N.message.no),
                                                                                    t(RequestI18N.grade.table.nf),
                                                                                    "-"
                                                                            ).value
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </div>
                                                    )}
                                                </div>
                                            }
                                            checkQuoteDiff={(quote) =>
                                                    quote.orderData?.cartonGrade !== model.orderData?.cartonGrade
                                            }
                                            renderQuoteValue={(quote) => (
                                                    <QuoteCartonGradeValue quote={quote} model={model} locale={locale}/>
                                            )}
                                    />
                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.field.closureType)}
                                            requestValue={
                                                stringArrayField(
                                                        [
                                                            booleanField(
                                                                    model?.orderData?.glued,
                                                                    t(RequestI18N.details.field.glue),
                                                                    "",
                                                                    ""
                                                            ).value,
                                                            booleanField(
                                                                    model?.orderData?.stitched,
                                                                    t(RequestI18N.details.field.staple),
                                                                    "",
                                                                    ""
                                                            ).value,
                                                            booleanField(
                                                                    model?.orderData?.taped,
                                                                    t(RequestI18N.details.field.tap),
                                                                    "",
                                                                    ""
                                                            ).value
                                                        ],
                                                        ""
                                                ).value
                                            }
                                            checkQuoteDiff={(quote) =>
                                                    `${quote.orderData?.glued}${quote.orderData?.stitched}${quote.orderData?.taped}` !==
                                                    `${model.orderData?.glued}${model.orderData?.stitched}${model.orderData?.taped}`
                                            }
                                            renderQuoteValue={(quote) =>
                                                    stringArrayField(
                                                            [
                                                                booleanField(
                                                                        quote.orderData?.glued,
                                                                        t(RequestI18N.details.field.glue),
                                                                        "",
                                                                        ""
                                                                ).value,
                                                                booleanField(
                                                                        quote.orderData?.stitched,
                                                                        t(RequestI18N.details.field.staple),
                                                                        "",
                                                                        ""
                                                                ).value,
                                                                booleanField(quote.orderData?.taped, t(RequestI18N.details.field.tap), "", "")
                                                                        .value
                                                            ],
                                                            ""
                                                    ).value
                                            }
                                    />

                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.field.palletType)}
                                            requestValue={
                                                stringField(
                                                        model?.orderData?.palletType &&
                                                        t(CommonI18N.enum.palletType[model?.orderData?.palletType]),
                                                        "palletType"
                                                ).value
                                            }
                                            checkQuoteDiff={(quote) =>
                                                    quote.orderData?.palletType !== model.orderData?.palletType
                                            }
                                            renderQuoteValue={(quote) =>
                                                    stringField(
                                                            quote.orderData?.palletType &&
                                                            t(CommonI18N.enum.palletType[quote.orderData?.palletType]),
                                                            "palletType"
                                                    ).value
                                            }
                                    />

                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.field.palletHeight)}
                                            requestValue={
                                                integerField(model?.orderData?.palletHeight, "palletHeight", undefined, locale)
                                                        .value
                                            }
                                            checkQuoteDiff={(quote) =>
                                                    quote.orderData?.palletHeight !== model.orderData?.palletHeight
                                            }
                                            renderQuoteValue={(quote) =>
                                                    integerField(quote.orderData?.palletHeight, "palletHeight", undefined, locale)
                                                            .value
                                            }
                                    />

                                    {model.orderData.separateExtraCosts ? (
                                            <>
                                                <QuoteMatrixRow
                                                        quotes={quotes}
                                                        label={t(RequestI18N.details.field.toolingCosts)}
                                                        requestValue={
                                                            currencyField(
                                                                    model?.orderData?.toolingCosts,
                                                                    "toolingCostsLabel",
                                                                    undefined,
                                                                    locale
                                                            ).value
                                                        }
                                                        checkQuoteDiff={(quote) =>
                                                                quote.orderData?.toolingCosts !== model.orderData?.toolingCosts
                                                        }
                                                        renderQuoteValue={(quote) =>
                                                                currencyField(
                                                                        quote.orderData?.toolingCosts,
                                                                        "toolingCostsLabel",
                                                                        undefined,
                                                                        locale
                                                                ).value
                                                        }
                                                />

                                                <QuoteMatrixRow
                                                        quotes={quotes}
                                                        label={t(RequestI18N.details.field.clicheCosts)}
                                                        requestValue={
                                                            currencyField(
                                                                    model?.orderData?.clicheCosts,
                                                                    "clicheCostsLabel",
                                                                    undefined,
                                                                    locale
                                                            ).value
                                                        }
                                                        checkQuoteDiff={(quote) =>
                                                                quote.orderData?.clicheCosts !== model.orderData?.clicheCosts
                                                        }
                                                        renderQuoteValue={(quote) =>
                                                                currencyField(
                                                                        quote.orderData?.clicheCosts,
                                                                        "clicheCostsLabel",
                                                                        undefined,
                                                                        locale
                                                                ).value
                                                        }
                                                />
                                            </>
                                    ) : null}

                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.label.prices)}
                                            requestValue={<Prices quantityOptions={model?.orderData?.quantityOptions}
                                                                  t={t} locale={locale}/>}
                                            checkQuoteDiff={
                                                (quote) => false
                                            }
                                            renderQuoteValue={(quote) => (
                                                    <Prices
                                                            quantityOptions={quote?.orderData?.quantityOptions}
                                                            orgOptions={model?.orderData?.quantityOptions}
                                                            t={t}
                                                            locale={locale}
                                                    />
                                            )}
                                    />

                                    <QuoteMatrixRow
                                            quotes={quotes}
                                            label={t(RequestI18N.details.field.status)}
                                            requestValue={<RequestStatusBadge status={model?.status} fullWidth/>}
                                            checkQuoteDiff={
                                                (quote) => false
                                            }
                                            renderQuoteValue={(quote) => <QuoteStatusBadge status={quote.status}
                                                                                           fullWidth/>}
                                    />
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </ShortCutHandler>
                </DialogContent>
            </Dialog>
    );
};
export default withAuthWrapperComponent(QuoteMatrixDialog);
